import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FaBell, FaMinusCircle } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../Components/BackButton';
import CreateReminderModal from '../../../Components/CreateReminderModal/CreateReminderModal';
import CustomButton from '../../../Components/CustomButton';
import CustomTable from '../../../Components/CustomTable/CustomTable';
import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import withFilters from '../../../HOC/withFilters ';
import withModal from '../../../HOC/withModal';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getReminders } from '../../../Services/User/Chat';
import { remindersHeaders } from '../../../Utils/Constants/TableHeaders';
import { serialNum } from '../../../Utils/Utils';
import './Reminders.css';

const Reminders = ({ filters, setFilters, pagination, updatePagination }) => {
  usePageTitle('Reminders');
  const navigate = useNavigate();
  const { chatId } = useParams();

  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Set default per_page to 15 if not set
  useEffect(() => {
    if (!filters?.per_page || filters.per_page !== 15) {
      setFilters(prev => ({ ...prev, per_page: 15 }));
    }
  }, []);

  useEffect(() => {
    if (chatId) {
      fetchReminders();
    }
  }, [filters, chatId]);

  const fetchReminders = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: filters?.page || 1,
        per_page: filters?.per_page || 15,
      };

      const response = await getReminders(chatId, params);
      
      if (response.status && response.data) {
        setReminders(response.data);
        
        // Update pagination with API response
        if (response.meta && updatePagination) {
          updatePagination({
            currentPage: response.meta.current_page,
            totalPages: response.meta.last_page,
            perPage: response.meta.per_page,
            totalRecords: response.meta.total,
            showData: response.meta.to,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    // Format date as MM/DD/YYYY
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleCreateReminder = () => {
    setShowCreateModal(true);
  };

  const handleReminderCreated = () => {
    // Refresh reminders list after creating
    fetchReminders();
  };

  const handleViewReminder = (reminderId) => {
    // TODO: Navigate to view reminder details or open modal
    console.log('View reminder:', reminderId);
  };

  const handleEditReminder = (reminderId) => {
    // TODO: Navigate to edit reminder or open modal
    console.log('Edit reminder:', reminderId);
  };

  return (
    <>
      <InnerBanner heading="Reminders" />
      <section className="py-sm-5 py-4">
        <div className="container">
          <BackButton onClick={() => navigate('/chat')} />
          <div className="d-card reminders-section">
            <div className="d-flex justify-content-between flex-wrap mb-3 align-items-center">
              <h2 className="screen-title mb-0">Upcoming Reminders</h2>
              <CustomButton
                onClick={handleCreateReminder}
                className="create-reminder-btn"
              >
                Create New Reminders
              </CustomButton>
            </div>

            <Row>
              <Col xs={12}>
                <CustomTable
                  filters={filters}
                  setFilters={setFilters}
                  headers={remindersHeaders}
                  pagination={pagination}
                  isLoading={isLoading}
                  useApplyButton={true}
                  dateFilters={[{ title: 'Date', from: 'from', to: 'to' }]}
                >
                  <tbody>
                    {reminders?.length > 0 ? (
                      reminders.map((item, index) => (
                        <tr key={item.id}>
                          <td>
                            {serialNum(
                              ((pagination?.currentPage || filters?.page || 1) - 1) * 
                              (pagination?.perPage || filters?.per_page || 15) + 
                              index + 1
                            )}
                          </td>
                          <td>{item?.title || '-'}</td>
                          <td>{item?.description || '-'}</td>
                          <td>{formatDateOnly(item?.reminder_date)}</td>
                          <td>{formatTime(item?.reminder_time)}</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              <button className="transparent-btn">
                                <FaMinusCircle className='grey-text' />
                              </button>
                              <button className="transparent-btn">
                                <FaBell className='grey-text' />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={remindersHeaders.length} className="text-center py-4">
                          {isLoading ? 'Loading...' : 'No reminders found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </CustomTable>
              </Col>
            </Row>
          </div>
        </div>
      </section>

      {/* Create Reminder Modal */}
      <CreateReminderModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        chatId={chatId}
        onSuccess={handleReminderCreated}
      />
    </>
  );
};

export default withModal(withFilters(Reminders));

